import { useEffect, useState } from 'react'
import { syncOutbox } from '../lib/api'
import { isOnline, onConnectivityChange, onOutboxChange, type OutboxEntry } from '../lib/offline'
import './offline-bar.css'

/* A single honest line about connectivity, shown only when there is something
 * to say: the device is offline, or work is waiting to be sent. It reports
 * state rather than nagging - no dismiss button, because it disappears by
 * itself the moment the situation resolves. */
export default function OfflineBar() {
  const [online, setOnline] = useState(isOnline)
  const [pending, setPending] = useState<OutboxEntry[]>([])
  const [syncing, setSyncing] = useState(false)

  useEffect(() => onConnectivityChange(setOnline), [])
  useEffect(() => onOutboxChange(setPending), [])

  const stuck = pending.filter((e) => e.lastError)
  const waiting = pending.filter((e) => !e.lastError)

  if (online && pending.length === 0) return null

  async function retry() {
    setSyncing(true)
    try {
      await syncOutbox()
    } finally {
      setSyncing(false)
    }
  }

  const tone = !online ? 'offline' : stuck.length ? 'stuck' : 'pending'

  return (
    <div className={`offbar offbar--${tone}`} role="status" aria-live="polite">
      <span className="offbar__dot" aria-hidden />
      <span className="offbar__text">
        {!online && waiting.length > 0 && (
          <>No connection — {waiting.length} {waiting.length === 1 ? 'entry' : 'entries'} saved
          on this device and will be sent automatically.</>
        )}
        {!online && waiting.length === 0 && (
          <>No connection — you can keep working, and what you record will be sent when you are back online.</>
        )}
        {online && waiting.length > 0 && (
          <>Sending {waiting.length} saved {waiting.length === 1 ? 'entry' : 'entries'}…</>
        )}
        {online && waiting.length === 0 && stuck.length > 0 && (
          <>{stuck.length} saved {stuck.length === 1 ? 'entry' : 'entries'} could not be sent: {stuck[0].lastError}</>
        )}
      </span>
      {online && pending.length > 0 && (
        <button type="button" className="offbar__btn" onClick={retry} disabled={syncing}>
          {syncing ? 'Sending…' : 'Retry now'}
        </button>
      )}
    </div>
  )
}
